<template>
  <div class="container">
    <div id="print_invoice">
      <div class="ticket-container">

        <!-- ── KITCHEN ORDER VIEW ─────────────────────────────────────────── -->
        <div v-if="isKitchenReceipt">
          <div v-for="t in kitchenTickets" :key="t.id" class="ticket">
            <hr class="mt-5" />
            <div class="text-center">
              <h3 v-if="t.flag" class="text-danger">** {{ t.flag }} **</h3>
              <h3>Kitchen Order</h3>
              <h3 v-if="t.station" class="text-uppercase">{{ t.station }}</h3>
              <h2>Order Number: {{ order.order }}</h2>
            </div>
            <p>Sent: {{ formatDate(t.sentAt, 'DD-MM-Y hh:mm A') }}</p>
            <p>Order by: <span class="text-capitalize">{{ order.soldby?.name }}</span></p>
            <hr class="my-1" />
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th class="quantity">QTY</th>
                  <th class="description">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in t.items" :key="item.id">
                  <td class="quantity h5 font-weight-bold">{{ item.qty }}</td>
                  <td class="description h5 font-weight-bold">
                    {{ item.name }}
                    <div v-if="item.notes" class="small font-weight-normal">&gt; {{ item.notes }}</div>
                  </td>
                </tr>
                <tr>
                  <td class="totals" colspan="2"><b>({{ t.totalQty }}) - Ttl QTY</b></td>
                </tr>
              </tbody>
            </table>
            <p v-for="(n, i) in kitchenNotes" :key="i"><b>NOTE:</b> {{ n }}</p>
          </div>
        </div>

        <!-- ── SALES RECEIPT VIEW ─────────────────────────────────────────── -->
        <div v-else class="ticket">
          <div v-if="hasCompany && showLogo" class="text-center">
            <img
              v-if="user.company.logo_url"
              class="centered"
              :src="user.company.logo_url"
              style="width:auto;height:auto;max-height:120px"
              alt="Logo"
            />
            <p v-else class="centered">Logo</p>
          </div>

          <p v-if="hasCompany" class="centered text-center">
            <span class="company-details">{{ user.company.name }}</span>
            <span v-if="docSettings.document_tagline"><br /><strong><em>{{ docSettings.document_tagline }}</em></strong></span>
            <span v-if="docSettings.document_description"><br /><small>{{ docSettings.document_description }}</small></span>
            <span v-if="user.company.phone"><br />Phone: {{ user.company.phone }}</span>
            <span v-if="user.company.email"><br />Email: {{ user.company.email }}</span>
            <span v-if="user.company.address"><br />Address: {{ user.company.address }}</span>
            <span v-if="user.company.building"><br />Location: {{ user.company.building }}</span>
            <span v-if="user.company.street">{{ user.company.street }}</span>
            <span v-if="user.company.town"><br />Town: {{ user.company.town }}</span>
            <span v-if="user.company.krapin"><br />PIN: {{ user.company.krapin }}</span>
          </p>

          <template v-if="order.customer">
            <hr class="my-1" />
            <h6>Bill To:</h6>
            <p>Name: {{ order.customer.full_name }}</p>
            <p v-if="order.customer.phone">Phone: {{ order.customer.phone }}</p>
            <p v-if="order.customer.email">Email: {{ order.customer.email }}</p>
            <p v-if="order.customer.company">Company: {{ order.customer.company }}</p>
            <p v-if="order.customer.krapin">Pin: {{ order.customer.krapin }}</p>
            <p v-if="customerStreet(order.customer)">Address: {{ customerStreet(order.customer) }}</p>
            <p v-if="order.customer.town">Town: {{ order.customer.town }}</p>
          </template>

          <hr class="my-1" />
          <p>Order Number: {{ order.order }}</p>
          <p>Date: {{ formatDate(order.created_at, 'DD-MM-Y') }}</p>
          <p>Time: {{ formatDate(order.created_at, 'hh:mm A') }}</p>
          <hr class="my-1" />

          <table class="table table-bordered">
            <thead>
              <tr>
                <th class="quantity">QTY</th>
                <th class="description">Description</th>
                <th class="price">{{ currency }}</th>
                <th class="totals">Totals</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderitems" :key="item.id">
                <td class="quantity">{{ item.qty }}</td>
                <td class="description">
                  {{ itemName(item) }}
                </td>
                <td class="price">{{ itemUnitPrice(item) | toFixed2 }}</td>
                <td class="price">{{ (itemUnitPrice(item) * item.qty) | toFixed2 }}</td>
              </tr>

              <tr>
                <td class="totals" colspan="3"><b>({{ totalQuantity }}) - Ttl QTY</b></td>
                <td class="totals"></td>
              </tr>
              <tr v-if="order.discount != null">
                <td class="totals" colspan="3"><b>Discount</b></td>
                <td class="totals">{{ order.discount }}</td>
              </tr>
              <tr>
                <td class="totals" colspan="3"><b>Sub Total</b></td>
                <td class="totals">{{ correctSubTotal | toFixed2 }}</td>
              </tr>
              <template v-if="storeSettings.enable_taxes">
                <tr v-for="tax in order.tax_ledgers" :key="tax.id">
                  <td class="totals" colspan="3">{{ tax.description }}</td>
                  <td class="totals">{{ tax.credit }}</td>
                </tr>
              </template>
              <tr v-if="order.partial && order.balance > 0">
                <td class="totals" colspan="3">Balance</td>
                <td class="totals">{{ order.balance }}</td>
              </tr>
              <tr>
                <td class="totals" colspan="3"><b>Total</b></td>
                <td class="totals">{{ order.total_amount }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="order.partial && order.balance > 0">
            <h1 class="text-center text-danger">NOT CLEARED</h1>
          </div>

          <template v-if="order.paymentmethod">
            <h5>Payment</h5>
            <p>Payment: {{ order.paymentmethod.payment_method }}</p>
            <p>Amount: {{ order.total_amount }}</p>
            <template v-if="order.cash_received > 0">
              <p>Cash Received: {{ order.cash_received | toFixed2 }}</p>
              <p>Change Given: {{ (order.change_given || 0) | toFixed2 }}</p>
            </template>
            <p v-if="order.transaction_validated && order.transaction_validated.transaction_code">
              Txn Code: {{ order.transaction_validated.transaction_code }}
            </p>
          </template>

          <hr />
          <h5>Notes</h5>
          <ul class="list-group">
            <template v-for="note in order.ordernotes">
              <li v-if="note.on_receipt" :key="note.id" class="list-group-item">{{ note.note }}</li>
            </template>
          </ul>
          <hr />
          <p class="text-justify">
            You were served by: <span class="text-capitalize">{{ order.soldby?.name }}</span>
          </p>
          <hr />
          <p v-if="hasCompany" class="text-justify" v-html="user.company.configs"></p>

          <!-- ── FISCAL / eTIMS BLOCK ──────────────────────────────────────── -->
          <template v-if="fiscalQrProxyUrl || (fiscalTransaction && (fiscalTransaction.control_invoice_number || fiscalTransaction.control_serial))">
            <hr class="my-1" />
            <div class="text-center">
              <img
                v-if="fiscalQrProxyUrl"
                :src="fiscalQrProxyUrl"
                alt="eTIMS QR"
                style="width:120px;height:120px;margin-bottom:6px"
              />
              <p class="mb-1"><strong>Control Info</strong></p>
              <p v-if="fiscalTransaction && fiscalTransaction.control_invoice_number" class="mb-0">
                Invoice #: {{ fiscalTransaction.control_invoice_number }}
              </p>
              <p v-if="fiscalTransaction && fiscalTransaction.control_serial" class="mb-0">
                Serial: {{ fiscalTransaction.control_serial }}
              </p>
              <p v-if="fiscalTransaction && fiscalTransaction.control_datetime" class="mb-0">
                Date: {{ fiscalTransaction.control_datetime }}
              </p>
            </div>
          </template>
          <!-- ── END FISCAL BLOCK ───────────────────────────────────────────── -->
        </div>
      </div>

      <!-- ── ACTION BAR ─────────────────────────────────────────────────────── -->
      <div class="container text-center mt-3">
        <p v-if="fiscalLoadingStatus" class="text-primary">{{ fiscalLoadingStatus }}</p>
        <p v-if="posPrintStatus" class="" :class="printStatusClass">{{ posPrintStatus }}</p>

        <div class="btn-group px-2 flex-wrap" style="gap:4px">
          <a href="/makesale" class="btn btn-primary">
            <i class="bi bi-cart"></i> Make a sale
          </a>
    <!-- Only shown when no vendor bridge AND no BLE connected -->
          <button
            v-if="!hasBridge && !bluetoothPrinter.characteristic"
            class="btn btn-secondary"
            @click="connectToPrinter"
          >
            <i class="bi bi-bluetooth"></i> Connect Bluetooth Printer
          </button>
          <button
            class="btn btn-primary print-action"
            :disabled="posPrinting || hasTims"
            @click="triggerPrint"
          >
            <i v-if="posPrinting" class="bi bi-hourglass-split"></i>
            <i v-else class="bi bi-printer-fill"></i>
            {{ posPrinting ? 'Printing…' : 'Print Receipt' }}
          </button>

          <!-- Wallet payment — shown when feature is on, customer exists, and balance remains -->
          <button
            v-if="walletEnabled && order.partial && order.balance > 0"
            class="btn btn-outline-success"
            @click="$refs.walletPayModal.show()"
          >
            <i class="bi bi-wallet2"></i> Pay with Wallet
          </button>

          <!-- Generate Fiscal Receipt — shown when TIMS is enabled and receipt is not yet completed -->
          <button
            v-if="storeSettings.enable_tims && canGenerateFiscalReceipt"
            class="btn btn-outline-info"
            :disabled="fiscalActionLoading"
            @click="generateFiscalReceipt"
          >
            <i v-if="fiscalActionLoading" class="bi bi-hourglass-split"></i>
            <i v-else class="bi bi-receipt"></i>
            {{ fiscalActionLoading ? 'Processing…' : (fiscalTransaction && fiscalTransaction.status === 'failed' ? 'Retry Fiscal Receipt' : 'Generate Fiscal Receipt') }}
          </button>

          <!-- Issue Credit Note — shown when a completed fiscal receipt exists and no credit note yet -->
          <button
            v-if="storeSettings.enable_tims && canIssueCreditNote"
            class="btn btn-outline-warning"
            :disabled="fiscalActionLoading"
            @click="issueCreditNote"
          >
            <i v-if="fiscalActionLoading" class="bi bi-hourglass-split"></i>
            <i v-else class="bi bi-receipt-cutoff"></i>
            {{ fiscalActionLoading ? 'Processing…' : 'Issue Credit Note' }}
          </button>
        </div>

        <!-- Print count indicator -->
        <p v-if="printCountLabel" class="mt-2 text-muted">
          <i class="bi bi-printer mr-1"></i>{{ printCountLabel }}
          <span v-if="isPrintBlocked" class="text-danger ml-1">(limit reached — employee approval required)</span>
        </p>

        <!-- Print limit override — employee code + PIN approval -->
        <b-modal
          id="print-approval-modal"
          title="Approve Print"
          hide-footer
          no-close-on-backdrop
          @hidden="_cancelPrintApproval"
        >
          <p class="text-muted mb-3">
            This receipt has reached its print limit. Enter an employee code and PIN with
            permission to approve additional prints.
          </p>
          <b-form-group label="Employee Code">
            <b-form-input v-model="printApproval.code" autofocus @keyup.enter="_confirmPrintApproval" />
          </b-form-group>
          <b-form-group label="Employee PIN">
            <b-form-input v-model="printApproval.pin" type="password" @keyup.enter="_confirmPrintApproval" />
          </b-form-group>
          <p v-if="printApproval.error" class="text-danger small">{{ printApproval.error }}</p>
          <div class="text-right">
            <b-button variant="secondary" class="mr-2" @click="_cancelPrintApproval">Cancel</b-button>
            <b-button variant="primary" @click="_confirmPrintApproval">Approve &amp; Print</b-button>
          </div>
        </b-modal>

        <p v-if="bluetoothPrinter.status && !hasBridge" class="mt-2 text-muted">
          Bluetooth: {{ bluetoothPrinter.status }}
        </p>
        <p v-if="imageLoadingStatus" class="text-muted">{{ imageLoadingStatus }}</p>
      </div>
    </div>

    <!-- Wallet payment modal -->
    <b-modal ref="walletPayModal" hide-footer title="Pay with Customer Wallet">
      <div class="mb-3">
        <strong>Remaining balance:</strong> KES {{ order.balance }}
      </div>
      <div class="mb-3">
        <strong>Wallet balance:</strong>
        KES {{ walletBalance !== null ? walletBalance : '…' }}
      </div>
      <div class="form-group">
        <label>Amount to deduct from wallet</label>
        <input
          type="number"
          class="form-control"
          v-model="walletPaymentAmount"
          :placeholder="'Max ' + order.balance"
          min="0.01"
          step="0.01"
        />
      </div>
      <small v-if="walletPaymentError" class="text-danger d-block mb-2">{{ walletPaymentError }}</small>
      <button
        class="btn btn-primary btn-block"
        :disabled="walletPaymentLoading || !walletPaymentAmount"
        @click="payWithWallet"
      >
        <span v-if="walletPaymentLoading" class="spinner-border spinner-border-sm mr-1"></span>
        Confirm Payment
      </button>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

// ─────────────────────────────────────────────────────────────────────────────
// Module-level ESC/POS constants — defined once, shared across all methods
// ─────────────────────────────────────────────────────────────────────────────
const ESC      = '\x1B';
const GS       = '\x1D';
const NL       = '\x0A';
const CRNL     = '\r\n';
const INIT     = `${ESC}@`;
const CUT      = `${NL}${NL}${NL}${GS}V\x42\x00`;
const BOLD_ON  = `${ESC}!\x08`;
const BOLD_OFF = `${ESC}!\x00`;
const ALIGN_L  = `${ESC}a\x00`;
const ALIGN_C  = `${ESC}a\x01`;
const FONT_2X  = `${ESC}!\x30`;
const FONT_NRM = `${ESC}!\x00`;

// ─────────────────────────────────────────────────────────────────────────────
// ESC/POS text helpers (pure functions — no Vue dependency)
// ─────────────────────────────────────────────────────────────────────────────
const escPadEnd   = (s, n) => { s = String(s); return s.length >= n ? s.slice(0, n) : s + ' '.repeat(n - s.length); };
const escPadStart = (s, n) => { s = String(s); return s.length >= n ? s.slice(0, n) : ' '.repeat(n - s.length) + s; };
const escCenter   = (t, w = 32) => ' '.repeat(Math.max(0, Math.floor((w - String(t).length) / 2))) + t;
const escDivider  = (w = 32) => '-'.repeat(w);
// Word-wrap to w chars; words longer than a line are split.
const escWrap     = (text, w) => {
  const lines = [];
  let cur = '';
  for (let word of String(text || '').split(/\s+/).filter(Boolean)) {
    while (word.length > w) {
      if (cur) { lines.push(cur); cur = ''; }
      lines.push(word.slice(0, w)); word = word.slice(w);
    }
    if (!cur) cur = word;
    else if (cur.length + 1 + word.length <= w) cur += ' ' + word;
    else { lines.push(cur); cur = word; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
};
const escCol2     = (l, v, w = 32) => { l = String(l); v = String(v); return l + ' '.repeat(Math.max(1, w - l.length - v.length)) + v; };
const escCol4     = (a, b, c, d, W = [4, 14, 7, 7]) =>
  escPadEnd(a, W[0]) + escPadEnd(b, W[1]) + escPadStart(c, W[2]) + escPadStart(d, W[3]);

// ─────────────────────────────────────────────────────────────────────────────
// Shared item helpers (used by both ESC/POS builders and HTML builder)
// ─────────────────────────────────────────────────────────────────────────────
function getItemName (item) {
  return item.product
    ? item.product.product
    : (item.grouped_inventory ? item.grouped_inventory.group_title : 'N/A');
}

function getItemUnitPrice (item) {
  if (item.product) {
    return parseFloat(item.selling_price != null
      ? item.selling_price
      : (item.is_retail === 1 ? item.product.ppu : item.product.wholesale_sp));
  }
  const gi = item.grouped_inventory;
  return parseFloat(item.selling_price != null
    ? item.selling_price
    : (item.is_retail === 1 ? (gi ? gi.cost : 0) : (gi ? gi.wholesale : 0)));
}

// ─────────────────────────────────────────────────────────────────────────────
// Android vendor JS bridge detection
// Scans all known bridge objects and method names in one pass.
// ─────────────────────────────────────────────────────────────────────────────
const BRIDGE_OBJECTS = [
  'SunmiPrinter', 'sunmiInnerPrinter',  // Sunmi
  'iMinPrinter',                          // iMin
  'PaxPrinter',                           // PAX
  'AndroidPrinter', 'Android',            // Generic injected bridges
  'printer', 'Printer',
  'JSBridge', 'NativePrinter',
  'WebViewJavascriptBridge',
];
const BRIDGE_METHODS = ['printEscPos', 'printRaw', 'sendRawData', 'sendRAWData', 'printText', 'print', 'write'];

function detectBridge () {
  if (window.SunmiPrinter || window.sunmiInnerPrinter) return 'sunmi';
  if (window.iMinPrinter)  return 'imin';
  if (window.PaxPrinter)   return 'pax';
  for (const key of BRIDGE_OBJECTS.slice(4)) { // skip named vendors already checked
    const obj = window[key];
    if (obj && typeof obj === 'object') {
      for (const m of BRIDGE_METHODS) {
        if (typeof obj[m] === 'function') return 'android_generic';
      }
    }
  }
  return null;
}

function isAndroidWebView () {
  const ua = navigator.userAgent || '';
  return /Android/.test(ua) && (/wv\)/.test(ua) || /WebView/.test(ua));
}

// Dispatch ESC/POS bytes to the detected vendor bridge
async function printViaBridge (bridge, escposStr) {
  const bytes = new TextEncoder().encode(escposStr);

  if (bridge === 'sunmi') {
    const p = window.sunmiInnerPrinter || window.SunmiPrinter;
    if (typeof p.sendRAWData === 'function') {
      // base64-encode the raw bytes — Sunmi's preferred API
      p.sendRAWData(btoa(String.fromCharCode(...bytes)), null);
    } else if (typeof p.printText === 'function') {
      p.printText(escposStr, null);
      if (typeof p.cutPaper === 'function') p.cutPaper(null);
    }
    return;
  }

  if (bridge === 'imin') {
    window.iMinPrinter.sendRAWData(Array.from(bytes), () => {});
    return;
  }

  if (bridge === 'pax') {
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    window.PaxPrinter.printHex(hex);
    return;
  }

  // android_generic — find the first available object+method pair
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  for (const key of BRIDGE_OBJECTS.slice(4)) {
    const obj = window[key];
    if (!obj || typeof obj !== 'object') continue;
    if (typeof obj.printEscPos  === 'function') { obj.printEscPos(hex);      return; }
    if (typeof obj.printRaw     === 'function') { obj.printRaw(hex);         return; }
    if (typeof obj.sendRawData  === 'function') { obj.sendRawData(hex);      return; }
    if (typeof obj.sendRAWData  === 'function') { obj.sendRAWData(btoa(String.fromCharCode(...bytes)), null); return; }
    if (typeof obj.printText    === 'function') { obj.printText(escposStr);  return; }
    if (typeof obj.print        === 'function') { obj.print(escposStr);      return; }
  }
  throw new Error('android_generic bridge found during detection but no method matched during dispatch');
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable IndexedDB helper — opens once and caches the connection promise
// so multiple cache calls in the same session share one open() call.
// ─────────────────────────────────────────────────────────────────────────────
let _dbPromise = null;
function getImageDB () {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open('ReceiptImageCache', 1);
    req.onerror = () => { _dbPromise = null; reject(req.error); };
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images', { keyPath: 'url' })
          .createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
  return _dbPromise;
}

// ─────────────────────────────────────────────────────────────────────────────
// ESC/POS receipt builder (pure function — no Vue dependency)
// ─────────────────────────────────────────────────────────────────────────────
function buildEscPos (order, orderitems, user, storeSettings, totalQuantity, correctSubTotal, qrEscPos = '') {
  const company = user.company;
  const parts = [INIT, ALIGN_C];

  if (company && company.name) {
    parts.push(BOLD_ON, escCenter(company.name.toUpperCase()), NL, BOLD_OFF);
    if (storeSettings?.document_tagline)     parts.push(escCenter(storeSettings.document_tagline),     NL);
    if (storeSettings?.document_description) parts.push(escCenter(storeSettings.document_description), NL);
    if (company.phone)    parts.push(escCenter(`Tel: ${company.phone}`),    NL);
    if (company.email)    parts.push(escCenter(company.email),              NL);
    if (company.address)  parts.push(escCenter(company.address),            NL);
    if (company.building) parts.push(escCenter(company.building),           NL);
    if (company.town)     parts.push(escCenter(`Town: ${company.town}`),    NL);
    if (company.krapin)   parts.push(escCenter(`PIN: ${company.krapin}`),   NL);
  }

  parts.push(ALIGN_L, escDivider(), NL);

  if (order.customer) {
    const c = order.customer;
    parts.push(BOLD_ON, 'Bill To:', BOLD_OFF, NL);
    parts.push(`Name : ${c.full_name}`, NL);
    if (c.phone)   parts.push(`Phone: ${c.phone}`,   NL);
    if (c.email)   parts.push(`Email: ${c.email}`,   NL);
    if (c.company) parts.push(`Co.  : ${c.company}`, NL);
    if (c.krapin)  parts.push(`PIN  : ${c.krapin}`,  NL);
    const street = [c.apartment, c.address_address].map(v => (v || '').trim()).filter(Boolean).join(', ');
    if (street) parts.push(`Addr : ${street}`, NL);
    if (c.town)  parts.push(`Town : ${c.town}`, NL);
    parts.push(escDivider(), NL);
  }

  const created = order.created_at;
  const dt = created ? new Date(created) : new Date();
  parts.push(
    `Order #: ${order.order}`, NL,
    `Date   : ${dt.toLocaleDateString('en-GB')}`, NL,
    `Time   : ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`, NL,
    escDivider(), NL,
    BOLD_ON, escCol4('QTY', 'Item', storeSettings?.currency || 'Price', 'Total'), NL, BOLD_OFF,
    escDivider(), NL
  );

  for (const item of orderitems) {
    const name      = getItemName(item).substring(0, 14);
    const unitPrice = getItemUnitPrice(item);
    const lineTotal = (unitPrice * item.qty).toFixed(2);
    parts.push(escCol4(String(item.qty), name, unitPrice.toFixed(2), lineTotal), NL);

    // Product Variations + Units/Packaging (see
    // docs/product-variations-units-architecture.md §36 and
    // docs/product-variations-units-implementation-plan.md Part 11 —
    // Receipts): only shown when this line was actually sold in a
    // non-implicit unit (unit_label present, factor != 1) — a plain
    // base-unit sale prints exactly as before, unchanged.
    if (item.unit_label && item.conversion_factor_snapshot && parseFloat(item.conversion_factor_snapshot) !== 1) {
      const baseQty = item.base_quantity != null ? item.base_quantity : (item.qty * item.conversion_factor_snapshot);
      parts.push(`  (${item.qty} ${item.unit_label} = ${baseQty} pcs)`, NL);
    }

    if (item.grouped_inventory?.grouped_items) {
      for (const g of item.grouped_inventory.grouped_items) {
        if (g.product?.can_be_sold) parts.push(`  + ${g.product.product} x${g.count}`, NL);
      }
    }
  }

  parts.push(escDivider(), NL);
  parts.push(escCol2(`(${totalQuantity}) Total QTY`, ''), NL);
  if (order.discount != null) parts.push(escCol2('Discount:', parseFloat(order.discount).toFixed(2)), NL);
  parts.push(escCol2('Sub Total:', parseFloat(correctSubTotal).toFixed(2)), NL);

  if (storeSettings?.enable_taxes && order.tax_ledgers?.length) {
    for (const t of order.tax_ledgers) {
      parts.push(escCol2(`${t.description}:`, parseFloat(t.credit).toFixed(2)), NL);
    }
  }

  if (order.partial && order.balance > 0) parts.push(escCol2('Balance:', parseFloat(order.balance).toFixed(2)), NL);

  parts.push(BOLD_ON, escCol2('TOTAL:', parseFloat(order.total_amount).toFixed(2)), NL, BOLD_OFF, escDivider(), NL);

  if (order.partial && order.balance > 0) {
    parts.push(ALIGN_C, FONT_2X, '** NOT CLEARED **', NL, FONT_NRM, ALIGN_L);
  }

  if (order.paymentmethod) {
    parts.push(`Payment : ${order.paymentmethod.payment_method}`, NL, `Amount  : ${order.total_amount}`, NL);
    if (parseFloat(order.cash_received || 0) > 0) {
      parts.push(
        escCol2('Cash Rcvd:', parseFloat(order.cash_received).toFixed(2)), NL,
        escCol2('Change   :', parseFloat(order.change_given || 0).toFixed(2)), NL
      );
    }
    if (order.transaction_validated?.transaction_code) {
      parts.push(`Txn Code: ${order.transaction_validated.transaction_code}`, NL);
    }
    parts.push(escDivider(), NL);
  }

  if (order.ordernotes?.length) {
    for (const n of order.ordernotes) {
      if (n.on_receipt) parts.push(`* ${n.note}`, NL);
    }
    parts.push(escDivider(), NL);
  }

  parts.push(ALIGN_C, escCenter(`Served by: ${order.soldby?.name || ''}`), NL);
  if (company?.configs) parts.push(escCenter(company.configs.replace(/<[^>]*>/g, '')), NL);
  if (storeSettings?.receipt_footer_message) parts.push(escCenter(storeSettings.receipt_footer_message), NL);
  parts.push(ALIGN_L);

  const ft = order.fiscal_transaction;
  if (qrEscPos || ft?.control_serial || ft?.control_unit_info) {
    parts.push(escDivider(), NL);
    if (qrEscPos) parts.push(ALIGN_C, qrEscPos, NL, ALIGN_L);
    if (ft?.control_serial || ft?.control_unit_info) {
      parts.push(BOLD_ON, 'Control Info', BOLD_OFF, NL);
      if (ft.control_datetime)        parts.push(`Date    : ${ft.control_datetime}`,       NL);
      if (ft.control_invoice_number)  parts.push(`Inv #   : ${ft.control_invoice_number}`, NL);
      if (ft.control_serial)          parts.push(`Serial  : ${ft.control_serial}`,         NL);
      if (ft.control_unit_info)       parts.push(`CU Info : ${ft.control_unit_info}`,      NL);
    }
    parts.push(escDivider(), NL);
  }

  parts.push(CUT);
  return parts.join('');
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default {
  name: 'invoice',
  props: ['order', 'orderitems', 'user'],

  filters: {
    toFixed2 (v) { return parseFloat(v || 0).toFixed(2); },
  },

  data () {
    return {
      storeSettings:    {},
      kitchenOrders:    [],
      hasTims:          false,
      isFiscalReady:    false,
      fiscalLoadingStatus: '',
      fiscalPollingInterval: null,
      fiscalActionLoading: false,

      posPrintStatus:   '',
      posPrinting:      false,
      imageLoadingStatus: '',

      bluetoothPrinter: {
        device:         null,
        server:         null,
        service:        null,
        characteristic: null,
        status:         'Not connected',
        printerServiceUUID:        '000018f0-0000-1000-8000-00805f9b34fb',
        printerCharacteristicUUID: '00002af1-0000-1000-8000-00805f9b34fb',
      },

      // In-memory image cache (L1) — IndexedDB (L2) is accessed via getImageDB()
      _imageMemCache: {},

      // Print tracking state
      printStatus: {
        printCount: 0,
        limit:      null,   // null = unlimited
        blocked:    false,
      },

      // Employee code + PIN approval modal state (shown once the print limit is reached)
      printApproval: {
        code:    '',
        pin:     '',
        error:   '',
        resolve: null,
      },

      walletBalance: null,
      walletPaymentAmount: '',
      walletPaymentLoading: false,
      walletPaymentError: '',
    };
  },

  computed: {
    // Tagline / description shown under the business name.
    docSettings() {
      return (this.user && this.user.company && this.user.company.companysettings) || this.storeSettings || {};
    },
    ...mapGetters(['userDetails']),

    // Pure computed — no side effects
    hasCompany ()      { return this.user.company !== ''; },
    showLogo ()        { return this.storeSettings?.document_show_logo !== false; },
    currency ()        { return this.userDetails?.company?.companysettings?.currency || ''; },
    walletEnabled ()   {
      return !!this.storeSettings?.enable_customer_wallet && !!this.order?.customer;
    },
    hasBridge ()       { return !!detectBridge(); },
    isAndroidWebView() { return isAndroidWebView(); },

    // isKitchenReceipt is computed from URL params ONCE. Side-effect (KDS fetch)
    // is triggered in the watcher below, not here.
    isKitchenReceipt () {
      return new URLSearchParams(window.location.search).get('order-receipt') === 'true';
    },

    // One ticket per kitchen order (one per station). Without a kitchen-order
    // id (e.g. "Print Kitchen Order" from Orders) it falls back to one
    // ticket with every item on the sale.
    kitchenTickets () {
      if (this.kitchenOrders.length) {
        return this.kitchenOrders.map(k => {
          const items = (k.items || []).map(i => ({
            id:    i.id,
            qty:   i.quantity,
            name:  i.name || i.order_item?.product?.product || i.order_item?.grouped_inventory?.group_title || 'Item',
            notes: i.notes || '',
          }));
          return {
            id:       k.id,
            station:  k.prep_station && k.prep_station !== 'general' ? k.prep_station : '',
            sentAt:   k.sent_at || k.created_at,
            flag:     k.print_count > 0 ? 'REPRINT' : (k.is_addon ? 'ADD-ON' : ''),
            items,
            totalQty: items.reduce((a, i) => a + Number(i.qty || 0), 0),
          };
        });
      }
      const items = (this.orderitems || []).map(i => ({
        id: i.id, qty: i.qty, name: getItemName(i), notes: '',
      }));
      return [{
        id: 'order', station: '', sentAt: this.order?.created_at, flag: '',
        items, totalQty: items.reduce((a, i) => a + Number(i.qty || 0), 0),
      }];
    },

    kitchenNotes () {
      return (this.order?.ordernotes || []).map(n => n.note).filter(Boolean);
    },

    totalQuantity () {
      return (this.orderitems || []).reduce((a, i) => a + i.qty, 0);
    },

    correctSubTotal () {
      if (!this.order?.total_amount) return 0;
      const total = parseFloat(this.order.total_amount);
      if (!this.storeSettings.enable_taxes || !this.order.tax_ledgers?.length) return total;
      const taxes = this.order.tax_ledgers.reduce((s, t) => s + parseFloat(t.credit || 0), 0);
      return total - taxes;
    },

    printStatusClass () {
      const s = this.posPrintStatus.toLowerCase();
      if (s.includes('error') || s.includes('fail')) return 'text-danger';
      if (s.includes('✓'))                            return 'text-success';
      return 'text-info';
    },

    isPrintBlocked () {
      return this.printStatus.blocked;
    },

    printCountLabel () {
      const { printCount, limit } = this.printStatus;
      if (printCount === 0) return '';
      if (limit) return `Printed ${printCount}/${limit}`;
      return `Printed ${printCount}×`;
    },

    fiscalTransaction () {
      return this.order?.fiscal_transaction || null;
    },

    fiscalQrProxyUrl () {
      const url = this.fiscalTransaction?.qr_url?.trim();
      return url ? `/api/proxy-logo?remote_url=${encodeURIComponent(url)}` : null;
    },

    canGenerateFiscalReceipt () {
      const ft = this.fiscalTransaction;
      if (!ft) return true;
      return ft.status === 'failed';
    },

    canIssueCreditNote () {
      const ft = this.fiscalTransaction;
      if (!ft || ft.status !== 'completed') return false;
      if (ft.transaction_type === 'credit_note') return false;
      const all = this.order?.fiscal_transactions || [];
      return !all.some(t => t.transaction_type === 'credit_note' && t.status === 'completed');
    },
  },

  watch: {
    // Trigger KDS fetch reactively when the computed value is first available
    isKitchenReceipt: {
      immediate: true,
      handler (val) {
        if (val) {
          const ids = (new URLSearchParams(window.location.search).get('kitchen-order') || '')
            .split(',').map(x => x.trim()).filter(Boolean);
          if (ids.length) this._kitchenLoad = this._fetchKitchenOrders(ids);
        }
      },
    },
  },

  async mounted () {
    this._initImageDB();
    await this._loadPrintStatus(); // must resolve before auto-print fires
    this.getStoreSettings(); // loads storeSettings → calls checkTimsReadiness
  },

  beforeDestroy () {
    // Clean up polling interval to prevent memory leaks on unmount
    if (this.fiscalPollingInterval) {
      clearInterval(this.fiscalPollingInterval);
      this.fiscalPollingInterval = null;
    }
    // Gracefully disconnect BLE device
    if (this.bluetoothPrinter.device?.gatt?.connected) {
      this.bluetoothPrinter.device.gatt.disconnect();
    }
  },

  methods: {
    // House/apartment + street (town is printed on its own line). Skips
    // blanks and stray ", " from older rows.
    customerStreet(c) {
      if (!c) return '';
      return [c.apartment, c.address_address].map(v => (v || '').trim().replace(/^,|,$/g, '').trim()).filter(Boolean).join(', ');
    },

    // ══════════════════════════════════════════════════════════════════════
    // Template helpers (called from template to keep template clean)
    // ══════════════════════════════════════════════════════════════════════

    itemName (item)      { return getItemName(item); },
    itemUnitPrice (item) { return getItemUnitPrice(item); },
    formatDate (date, format) { return moment(date).format(format); },

    // ══════════════════════════════════════════════════════════════════════
    // PUBLIC PRINT ENTRY POINT
    // ══════════════════════════════════════════════════════════════════════

    async triggerPrint () {
      if (this.posPrinting) return;
      this.posPrinting = true;
      this.posPrintStatus = 'Preparing…';

      try {
        if (this.isKitchenReceipt) {
          await this._printKitchen();
        } else {
          await this._printReceipt();
        }
      } catch (err) {
        console.error('[invoice] print error:', err);
        this.posPrintStatus = `Print failed: ${err.message}`;
      } finally {
        this.posPrinting = false;
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // RECEIPT PRINT — builds ESC/POS and dispatches
    // ══════════════════════════════════════════════════════════════════════

    async _printReceipt () {
      // ── 1. Record print + enforce limit (server-authoritative) ────────────
      // This must resolve before we dispatch to the printer. Once the print
      // limit is reached, _recordPrint prompts for employee code + PIN and
      // retries with those credentials before giving up.
      if (this.order?.id) {
        await this._recordPrint();  // throws if cancelled/rejected → caught by triggerPrint
      }

      // ── 2. Build and dispatch to printer ──────────────────────────────────
      // BLE and HTML rebuild content themselves; only the bridge path consumes
      // the escpos string, so we pre-fetch the QR bitmap only when a bridge is
      // active — avoids a redundant network round-trip on other paths.
      let qrEscPos = '';
      if (detectBridge() && this.order?.fiscal_transaction?.qr_url?.trim()) {
        try {
          const qrBase64 = await this._fetchImage(
            `/api/proxy-logo?remote_url=${encodeURIComponent(this.order.fiscal_transaction.qr_url)}`,
            200
          );
          if (qrBase64) qrEscPos = await this._imageToEscPos(qrBase64, 200);
        } catch (e) {
          console.warn('[invoice] QR fetch for bridge failed', e);
        }
      }

      const escpos = buildEscPos(
        this.order, this.orderitems, this.user,
        this.storeSettings, this.totalQuantity, this.correctSubTotal,
        qrEscPos
      );
      await this._dispatch(escpos);
      this.posPrintStatus = 'Printed ✓';

      // ── 3. Fire-and-forget KDS status update ─────────────────────────────
      if (this.order?.id) {
        axios.post('/api/update-kds-order-item', { orderId: this.order.id })
          .catch(e => console.warn('[invoice] KDS update failed:', e));
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // KITCHEN ORDER PRINT
    // ══════════════════════════════════════════════════════════════════════

    async _printKitchen () {
      // Auto-print can fire before the tickets have loaded
      if (this._kitchenLoad) await this._kitchenLoad;

      const parts = [];
      for (const t of this.kitchenTickets) {
        const dt = t.sentAt ? new Date(t.sentAt) : new Date();
        parts.push(INIT, ALIGN_C);
        if (t.flag) parts.push(FONT_2X, `** ${t.flag} **`, NL, FONT_NRM);
        parts.push(BOLD_ON, 'KITCHEN ORDER', BOLD_OFF, NL);
        if (t.station) parts.push(FONT_2X, t.station.toUpperCase(), NL, FONT_NRM);
        parts.push(
          FONT_2X, `Order: ${this.order.order}`, NL, FONT_NRM,
          ALIGN_L, escDivider(), NL,
          `Sent: ${dt.toLocaleDateString('en-GB')} ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`, NL,
          `By  : ${this.order.soldby?.name || ''}`, NL,
          escDivider(), NL,
        );
        // Double-size text halves the line to 16 chars: quantity on the
        // left, the full name wrapped under itself — never cut off.
        for (const item of t.items) {
          const qty = String(item.qty).padEnd(3);
          escWrap(item.name, 13).forEach((line, i) => {
            parts.push(FONT_2X, (i === 0 ? qty : '   ') + line, NL, FONT_NRM);
          });
          if (item.notes) escWrap(`> ${item.notes}`, 28).forEach(l => parts.push('    ' + l, NL));
        }
        parts.push(escDivider(), NL, BOLD_ON, `Total QTY: ${t.totalQty}`, BOLD_OFF, NL);
        for (const n of this.kitchenNotes) {
          parts.push(escDivider(), NL);
          escWrap(`NOTE: ${n}`, 32).forEach(l => parts.push(BOLD_ON, l, BOLD_OFF, NL));
        }
        parts.push(CUT);
      }

      await this._dispatch(parts.join(''));
      this.posPrintStatus = 'Kitchen order printed ✓';
      this._recordKitchenPrint();
    },

    // Next print of these tickets will be flagged REPRINT
    _recordKitchenPrint () {
      for (const k of this.kitchenOrders) {
        this.$set(k, 'print_count', (k.print_count || 0) + 1);
        axios.post(`/api/kds/orders/${k.id}/printed`)
          .catch(e => console.warn('[invoice] kitchen print log failed:', e));
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // PRINT DISPATCHER
    // Priority: 1 → vendor JS bridge  2 → Bluetooth ESC/POS  3 → iframe HTML
    //
    // WebUSB intentionally excluded: built-in Android POS printers are never
    // exposed as WebUSB devices — requestDevice() always throws on POS hardware.
    // ══════════════════════════════════════════════════════════════════════

    async _dispatch (escposStr) {
      const bridge = detectBridge();

      if (bridge) {
        this.posPrintStatus = `Printing via ${bridge}…`;
        await printViaBridge(bridge, escposStr);
        return;
      }

      if (this.bluetoothPrinter.characteristic) {
        this.posPrintStatus = 'Printing via Bluetooth…';
        // _blePrint rebuilds the sales receipt; a kitchen ticket is already built
        if (this.isKitchenReceipt) await this._bleSend(escposStr);
        else await this._blePrint();
        return;
      }

      // HTML iframe fallback — works on all browsers and most Android POS WebViews
      this.posPrintStatus = 'Sending to printer…';
      await this._htmlPrint();
    },

    // ══════════════════════════════════════════════════════════════════════
    // BLUETOOTH — connect, send, receive
    // ══════════════════════════════════════════════════════════════════════

    async connectToPrinter () {
      try {
        this.bluetoothPrinter.status = 'Requesting device…';
        this.bluetoothPrinter.device = await navigator.bluetooth.requestDevice({
          filters: [{ services: [this.bluetoothPrinter.printerServiceUUID] }],
        });
        this.bluetoothPrinter.status = 'Connecting…';
        this.bluetoothPrinter.server = await this.bluetoothPrinter.device.gatt.connect();
        this.bluetoothPrinter.service = await this.bluetoothPrinter.server.getPrimaryService(
          this.bluetoothPrinter.printerServiceUUID
        );
        this.bluetoothPrinter.characteristic = await this.bluetoothPrinter.service.getCharacteristic(
          this.bluetoothPrinter.printerCharacteristicUUID
        );
        this.bluetoothPrinter.status = 'Connected ✓';
      } catch (e) {
        this.bluetoothPrinter.status = 'Connection failed. Try again.';
        console.warn('[invoice] BLE connect error:', e);
      }
    },

    // Full BLE print: fetch images → build ESC/POS → chunk-send
    async _blePrint () {
      if (!this.bluetoothPrinter.characteristic) throw new Error('Printer not connected');

      const [logoData, qrData] = await Promise.allSettled([
        (this.showLogo && this.user?.company?.logo_url)
          ? this._fetchImage(`/api/proxy-logo?remote_url=${encodeURIComponent(this.user.company.logo_url)}`, 384)
          : Promise.resolve(null),
        this.order?.fiscal_transaction?.qr_url
          ? this._fetchImage(`/api/proxy-logo?remote_url=${encodeURIComponent(this.order.fiscal_transaction.qr_url)}`, 200)
          : Promise.resolve(null),
      ]).then(results => results.map(r => r.status === 'fulfilled' ? r.value : null));

      const content = await this._buildBluetoothEscPos(logoData, qrData);
      await this._bleSend(content);
      this.bluetoothPrinter.status = 'Printed ✓';
    },

    // Chunk-send raw ESC/POS bytes over BLE (100-byte chunks is safe for all printers)
    async _bleSend (escposStr) {
      const bytes = new TextEncoder().encode(escposStr);
      const CHUNK = 100;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        await this.bluetoothPrinter.characteristic.writeValue(bytes.slice(i, i + CHUNK));
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // HTML IFRAME PRINT
    //
    // Browser compatibility:
    //   Desktop Chrome/Edge   → standard print dialog via onload
    //   Firefox               → standard print dialog via onload
    //   Safari / iOS Safari   → print dialog via onload; falls back to
    //                           window.print() if iframe.print() throws
    //   Android Chrome        → standard print dialog
    //   Android POS WebView   → routes directly to built-in printer driver,
    //                           skipping Chrome preview — this is the key fix
    //
    // The `printed` flag guarantees print() fires exactly ONCE even though
    // both onload and the 700ms safety timeout are armed simultaneously.
    // ══════════════════════════════════════════════════════════════════════

    async _htmlPrint () {
      // Pre-fetch the logo as base64 so it survives the srcdoc iframe context.
      // The QR is passed as a direct proxy URL instead — the browser loads it
      // natively inside the iframe, which is exactly how the on-screen <img>
      // works. _printViaIframe waits for all iframe images to load before
      // calling print(), so the QR will always be present in the print dialog.
      const [base64Logo] = await Promise.allSettled([
        (this.showLogo && this.user?.company?.logo_url?.trim())
          ? this._fetchImage(`/api/proxy-logo?remote_url=${encodeURIComponent(this.user.company.logo_url)}`, 300)
          : Promise.resolve(''),
      ]).then(results => results.map(r => r.status === 'fulfilled' ? r.value : ''));

      const qrProxyUrl = this.order?.fiscal_transaction?.qr_url?.trim()
        ? `/api/proxy-logo?remote_url=${encodeURIComponent(this.order.fiscal_transaction.qr_url)}`
        : '';

      const html = this._buildHtml(base64Logo, '', qrProxyUrl);
      this._printViaIframe(html);
    },

    _printViaIframe (htmlContent) {
      // ── Step 1: Kill any leftover iframe and wait for the Android
      // PrintManager spooler to clear the previous job before we create
      // a new one. Calling print() while a previous job is still in the
      // spooler is the root cause of the "printer pool error" on Android.
      const old = document.getElementById('_pos_print_frame');
      if (old) {
        old.onload = null; // prevent stale handler firing
        old.remove();
      }

      // Detect Android WebView for spooler-aware timing
      const isAndroid = /Android/.test(navigator.userAgent);

      // Spooler settle time: Android PrintManager needs ~800–1200ms after
      // the previous job before it will accept a new one without throwing
      // "printer pool error". Desktop/iOS need no delay.
      const SETTLE_MS  = isAndroid ? 1200 : 0;
      // Fallback timeout in case onload never fires (some Android WebViews
      // skip onload after srcdoc assignment)
      const FALLBACK_MS = isAndroid ? 2500 : 900;
      // How long to keep the iframe after print() so the spooler can finish
      // reading the document before we remove it (thermal spoolers are slow)
      const CLEANUP_MS  = isAndroid ? 8000 : 3000;

      const start = () => {
        const iframe = document.createElement('iframe');
        iframe.id    = '_pos_print_frame';

        // Width must match printer native pixel width so the print driver
        // receives correctly-sized content:
        //   203 dpi × 80mm ÷ 25.4 = 640px  (standard POS thermal at 203dpi)
        //   300 dpi × 80mm ÷ 25.4 = 945px  (high-res thermal at 300dpi)
        // We use 640px — correct for the vast majority of Android POS hardware.
        // height:auto lets the content define its own height (no clipping).
        // visibility:hidden keeps it off-screen but rendered at full fidelity
        // (opacity:0 + position:fixed can cause zero-size layout on some WebViews)
        // Width: 100vw fills whatever viewport the WebView allocates.
        // The @page CSS declaration in the document owns the real physical
        // paper width — the iframe just needs to not collapse the layout.
        // Do NOT hard-code a px width here — it fights with DPR scaling.
        iframe.style.cssText = [
          'position:fixed',
          'top:0',
          'left:0',
          'width:100vw',
          'height:100vh',
          'min-height:1px',
          'visibility:hidden',
          'pointer-events:none',
          'border:none',
          'z-index:-1',
        ].join(';');

        document.body.appendChild(iframe);

        let printed = false;

        const doPrint = () => {
          if (printed) return;
          printed = true;
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            // iOS Safari sometimes blocks iframe.print() — fall back to parent
            console.warn('[invoice] iframe.print() failed, falling back to window.print()', e);
            try { window.print(); } catch (_) { /* nothing more we can do */ }
          }
          setTimeout(() => {
            const el = document.getElementById('_pos_print_frame');
            if (el) el.remove();
          }, CLEANUP_MS);
        };

        const fallbackTimer = setTimeout(doPrint, FALLBACK_MS);

        iframe.onload = () => {
          clearTimeout(fallbackTimer);

          // After the document parses, wait for every <img> inside the iframe
          // to finish loading before calling print(). This is critical for QR
          // codes embedded as proxy URLs (not base64) — the browser fetches
          // them after parse, so onload fires before they are decoded.
          // doPrint() has its own `printed` guard, so it is safe to call from
          // both the image-load path and the safety timeout below.
          const proceed = () => {
            if (SETTLE_MS > 0) setTimeout(doPrint, SETTLE_MS);
            else doPrint();
          };

          try {
            const iDoc  = iframe.contentDocument || iframe.contentWindow?.document;
            const imgs  = iDoc ? Array.from(iDoc.images) : [];
            const pending = imgs.filter(img => !img.complete);

            if (!pending.length) {
              proceed();
            } else {
              let n = pending.length;
              const onDone = () => { if (!--n) proceed(); };
              pending.forEach(img => {
                img.addEventListener('load',  onDone, { once: true });
                img.addEventListener('error', onDone, { once: true });
              });
              // Safety: proceed after 5 s even if an image never resolves
              setTimeout(proceed, 5000);
            }
          } catch (e) {
            // Cross-origin or sandboxed iframe — fall through to immediate print
            proceed();
          }
        };

        // srcdoc is more reliable than doc.write on Chrome 90+ (Android WebView
        // is based on Chrome) — it sets content atomically and fires onload
        // consistently. Falls back to doc.write if srcdoc is not supported.
        if ('srcdoc' in iframe) {
          iframe.srcdoc = htmlContent;
        } else {
          const doc = iframe.contentWindow.document;
          doc.open();
          doc.write(htmlContent);
          doc.close();
        }
      };

      // On Android: let the current event loop tick clear before inserting the
      // iframe, giving the system a chance to release the previous print job.
      if (SETTLE_MS > 0) {
        setTimeout(start, 50);
      } else {
        start();
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // ESC/POS BLUETOOTH CONTENT BUILDER
    // Full-featured: supports logo image, QR code, grouped items, fiscal block
    // ══════════════════════════════════════════════════════════════════════

    async _buildBluetoothEscPos (logoBase64 = null, qrBase64 = null) {
      const company = this.user.company;
      const parts = [`${ESC}@`]; // Init

      if (logoBase64) {
        try { parts.push(await this._imageToEscPos(logoBase64, 384), CRNL); }
        catch (e) { console.warn('[invoice] logo ESC/POS conversion failed', e); }
      }

      parts.push(BOLD_ON);
      if (company?.name) parts.push(escCenter(company.name, 32), CRNL);
      parts.push(BOLD_OFF);

      if (company) {
        if (company.phone)    parts.push(escCenter(`Phone: ${company.phone}`, 32),    CRNL);
        if (company.email)    parts.push(escCenter(`Email: ${company.email}`, 32),    CRNL);
        if (company.address)  parts.push(escCenter(company.address, 32),              CRNL);
        if (company.building) parts.push(escCenter(company.building, 32),             CRNL);
        if (company.street)   parts.push(escCenter(company.street, 32),               CRNL);
        if (company.town)     parts.push(escCenter(`Town: ${company.town}`, 32),      CRNL);
        if (company.krapin)   parts.push(escCenter(`PIN: ${company.krapin}`, 32),     CRNL);
        parts.push(escDivider(), CRNL);
      }

      if (this.order.customer) {
        const c = this.order.customer;
        parts.push(BOLD_ON, `Bill To:${CRNL}`, BOLD_OFF);
        parts.push(`Name: ${c.full_name}${CRNL}`);
        if (c.phone)   parts.push(`Phone: ${c.phone}${CRNL}`);
        if (c.email)   parts.push(`Email: ${c.email}${CRNL}`);
        if (c.company) parts.push(`Company: ${c.company}${CRNL}`);
        if (c.krapin)  parts.push(`Pin: ${c.krapin}${CRNL}`);
        if (this.customerStreet(c)) parts.push(`Address: ${this.customerStreet(c)}${CRNL}`);
        if (c.town)    parts.push(`Town: ${c.town}${CRNL}`);
        parts.push(escDivider(), CRNL);
      }

      parts.push(
        `Order #: ${this.order.order}${CRNL}`,
        `Date: ${moment(this.order.created_at).format('D-MM-Y')}${CRNL}`,
        `Time: ${moment(this.order.created_at).format('h:mm a')}${CRNL}`,
        escDivider(), CRNL,
        BOLD_ON,
        this._formatCols(['QTY', 'Item', 'Price', 'Total'], [4, 14, 6, 7]), CRNL,
        BOLD_OFF,
        escDivider(), CRNL
      );

      for (const item of this.orderitems) {
        const name      = getItemName(item).substring(0, 14);
        const unitPrice = getItemUnitPrice(item);
        const lineTotal = (unitPrice * item.qty).toFixed(2);
        parts.push(this._formatCols(
          [String(item.qty), name, unitPrice.toFixed(2), lineTotal], [4, 14, 6, 7]
        ), CRNL);

        if (item.grouped_inventory?.grouped_items) {
          for (const g of item.grouped_inventory.grouped_items) {
            if (g.product?.can_be_sold) parts.push(`  - ${g.product.product} x${g.count}${CRNL}`);
          }
        }
      }

      parts.push(escDivider(), CRNL);
      parts.push(this._formatCols2('Total QTY:', String(this.totalQuantity)), CRNL);
      if (this.order.discount != null) {
        parts.push(this._formatCols2('Discount:', parseFloat(this.order.discount).toFixed(2)), CRNL);
      }
      parts.push(this._formatCols2('Sub Total:', parseFloat(this.correctSubTotal).toFixed(2)), CRNL);

      if (this.storeSettings.enable_taxes && this.order.tax_ledgers?.length) {
        for (const t of this.order.tax_ledgers) {
          parts.push(this._formatCols2(`${t.description}:`, parseFloat(t.credit).toFixed(2)), CRNL);
        }
      }

      if (this.order.partial && this.order.balance > 0) {
        parts.push(this._formatCols2('Balance:', parseFloat(this.order.balance).toFixed(2)), CRNL);
      }

      parts.push(
        BOLD_ON,
        this._formatCols2('TOTAL:', parseFloat(this.order.total_amount).toFixed(2)),
        CRNL, BOLD_OFF,
        escDivider(), CRNL
      );

      if (this.order.paymentmethod) {
        parts.push(
          `Payment: ${this.order.paymentmethod.payment_method}${CRNL}`,
          `Amount: ${this.order.total_amount}${CRNL}`
        );
        if (parseFloat(this.order.cash_received || 0) > 0) {
          parts.push(
            this._formatCols2('Cash Rcvd:', parseFloat(this.order.cash_received).toFixed(2)) + CRNL,
            this._formatCols2('Change   :', parseFloat(this.order.change_given || 0).toFixed(2)) + CRNL
          );
        }
        parts.push(escDivider(), CRNL);
      }

      if (this.order.ordernotes?.length) {
        for (const n of this.order.ordernotes) {
          if (n.on_receipt) parts.push(`* ${n.note}${CRNL}`);
        }
        parts.push(escDivider(), CRNL);
      }

      parts.push(escCenter(`Served by: ${this.order.soldby?.name || ''}`, 32), CRNL);
      if (company?.configs) parts.push(escCenter(company.configs.replace(/<[^>]*>/g, ''), 32), CRNL);
      if (this.storeSettings?.receipt_footer_message) {
        parts.push(escCenter(this.storeSettings.receipt_footer_message, 32), CRNL);
      }

      const ft = this.order.fiscal_transaction;
      if (qrBase64 || ft?.control_serial || ft?.control_unit_info) {
        parts.push(escDivider(), CRNL);
        if (qrBase64) {
          try { parts.push(await this._imageToEscPos(qrBase64, 200), CRNL); }
          catch (e) { console.warn('[invoice] QR ESC/POS conversion failed', e); }
        }
        if (ft?.control_serial || ft?.control_unit_info) {
          parts.push(BOLD_ON, `Control Info${CRNL}`, BOLD_OFF);
          if (ft.control_unit_info)      parts.push(`CU Info: ${ft.control_unit_info}${CRNL}`);
          if (ft.control_datetime)       parts.push(`Date: ${ft.control_datetime}${CRNL}`);
          if (ft.control_invoice_number) parts.push(`Invoice #: ${ft.control_invoice_number}${CRNL}`);
          if (ft.control_serial)         parts.push(`Serial: ${ft.control_serial}${CRNL}`);
        }
        parts.push(escDivider(), CRNL);
      }

      parts.push(CRNL, CRNL, CRNL, `${GS}V\x42\x00`); // cut
      return parts.join('');
    },

    // ESC/POS column formatters (instance methods for template compatibility,
    // but purely computational — no reactive access)
    _formatCols (cols, widths) {
      return cols.map((col, i) => {
        const t = String(col).substring(0, widths[i]);
        return i === cols.length - 1 ? t.padStart(widths[i]) : t.padEnd(widths[i]);
      }).join('');
    },
    _formatCols2 (label, value, total = 32) {
      label = String(label); value = String(value);
      return label + ' '.repeat(Math.max(1, total - label.length - value.length)) + value;
    },

    // ESC/POS public aliases (kept for backwards compatibility with any
    // external callers that may reference centerText / printLine / formatColumns)
    centerText (text, lineWidth = 32) { return escCenter(text, lineWidth); },
    printLine (width = 32)            { return escDivider(width); },
    formatColumns (columns, widths)   { return this._formatCols(columns, widths); },

    // ══════════════════════════════════════════════════════════════════════
    // IMAGE → ESC/POS RASTER BITMAP CONVERSION
    // Converts a base64 PNG/JPEG into ESC/POS GS v 0 raster bitmap bytes.
    // ══════════════════════════════════════════════════════════════════════

    _imageToEscPos (base64Image, maxWidth = 384) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const scale  = maxWidth / img.width;
            const w      = Math.min(img.width, maxWidth);
            const h      = Math.floor(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx    = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            const pixels     = ctx.getImageData(0, 0, w, h).data;
            const widthBytes = Math.ceil(w / 8);

            // Build GS v 0 header
            let out = `${ESC}a\x01`; // center align
            out += `${GS}v0\x00`;
            out += String.fromCharCode(widthBytes & 0xFF, (widthBytes >> 8) & 0xFF, h & 0xFF, (h >> 8) & 0xFF);

            // Convert to monochrome (1-bit) bitmap row by row
            for (let y = 0; y < h; y++) {
              for (let x = 0; x < w; x += 8) {
                let byte = 0;
                for (let b = 0; b < 8 && (x + b) < w; b++) {
                  const idx = (y * w + x + b) * 4;
                  const lum = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
                  if (lum < 128) byte |= (1 << (7 - b));
                }
                out += String.fromCharCode(byte);
              }
            }
            out += `${ESC}a\x00`; // left align
            resolve(out);
          } catch (e) { reject(e); }
        };
        img.onerror = reject;
        img.src = base64Image;
      });
    },

    // Kept as public alias for any external references
    convertImageToESCPOS (base64Image, maxWidth = 384) {
      return this._imageToEscPos(base64Image, maxWidth);
    },

    // ══════════════════════════════════════════════════════════════════════
    // HTML RECEIPT BUILDER (iframe / window.print fallback)
    // ══════════════════════════════════════════════════════════════════════

    _buildHtml (base64Logo = '', base64QR = '', qrSrc = '') {
      const company  = this.user.company;
      const currency = this.currency;
      const isKitchen = this.isKitchenReceipt;

      const CSS = `
/* ══════════════════════════════════════════════════════════════
   THERMAL RECEIPT CSS  —  80mm paper
   All sizes in pt (points = 1/72 inch, a physical absolute unit).
   pt bypasses Android WebView DPR scaling entirely, so 10pt body
   text prints as real 10pt on paper regardless of screen density.
   DO NOT use px here — px are device pixels and scale with DPR,
   causing tiny output on 2×/3× DPR screens.
══════════════════════════════════════════════════════════════ */

/* ── Page: 80mm roll, 3mm side margins, auto height ── */
@page {
  size: 80mm auto;
  margin: 0 3mm;
}

/* ── Reset ── */
*, *::before, *::after {
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ── Root
   width:100% fills the @page content area (80mm - 6mm margins = 74mm).
   font-size:10pt = ~3.5mm cap height on paper — ideal for 80mm thermal.
── */
html, body {
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10pt;
  line-height: 1.3;
  color: #000;
}

/* ── Typography ── */
h1, h2, h3, h4, h5, h6 { margin: 1.5pt 0; padding: 0; font-size: 11pt; font-weight: 700; }
p   { margin: 1pt 0; padding: 0; }
ul  { margin: 1.5pt 0; padding-left: 10pt; }
li  { margin: 1pt 0; }
b, strong { font-weight: 700; }
small { font-size: 8.5pt; }
hr  { border: none; border-top: 0.75pt solid #000; margin: 2pt 0; }
span { font-size: inherit; }

/* ── Utilities ── */
.tc  { text-align: center; }
.tj  { text-align: justify; }
.tca { text-transform: capitalize; }
.bold { font-weight: 700; }

/* ── Logo ── */
.logo { text-align: center; margin-bottom: 3pt; }
.logo img { max-width: 50mm; max-height: 20mm; width: auto; height: auto; }

/* ── QR code ── */
.qr { text-align: center; margin: 4pt 0; }
.qr img { width: 28mm; height: 28mm; }

/* ── Fiscal control info ── */
.ci { margin: 3pt 0; padding: 2pt 3pt; border: 0.75pt solid #000; }
.ci p { margin: 1pt 0; font-size: 9pt; }

/* ── Item table ── */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 2pt 0;
  /* No table-layout:fixed — let the browser measure column content naturally.
     Fixed layout with width:auto on col 2 was causing the browser to give
     the description column almost no space, breaking words mid-character. */
}
th {
  font-size: 9.5pt;
  font-weight: 700;
  padding: 1.5pt 2pt;
  border-top: 0.75pt solid #000;
  border-bottom: 0.75pt solid #000;
  white-space: nowrap;
}
td {
  font-size: 9.5pt;
  padding: 1.5pt 2pt;
  border-bottom: 0.5pt solid #ccc;
  vertical-align: top;
}
/* QTY: narrow fixed. Description: takes all remaining space, wraps at word
   boundaries only. Price and Total: fixed, right-aligned. */
table th:nth-child(1), table td:nth-child(1) { width: 7mm;  text-align: center; white-space: nowrap; }
table th:nth-child(2), table td:nth-child(2) { /* auto — fills remaining width */ overflow-wrap: break-word; word-break: normal; }
table th:nth-child(3), table td:nth-child(3) { width: 16mm; text-align: right; white-space: nowrap; }
table th:nth-child(4), table td:nth-child(4) { width: 16mm; text-align: right; white-space: nowrap; }

/* ── Totals footer ── */
tfoot td {
  font-size: 9.5pt;
  padding: 1.5pt 2pt;
  border-bottom: none;
}
tfoot tr:first-child td { border-top: 0.75pt solid #000; }
tfoot tr:last-child td  { border-top: 1.5pt solid #000; font-size: 11pt; font-weight: 700; }

/* ── Action footer ── */
.footer { margin-top: 3pt; text-align: center; font-size: 8.5pt; }

/* ── @media print: keep consistent — @page drives the real dimensions ── */
@media print {
  html, body { width: 100%; }
  .qr img { page-break-inside: avoid; }
}`;

      const rows = [];

      if (!isKitchen) {
        if (company) {
          rows.push(
            `<div class="logo tc">${base64Logo ? `<img src="${base64Logo}" alt="Logo"/>` : ''}</div>`,
            `<p class="tc"><span class="bold">${this._esc(company.name)}</span>`,
            company.phone    ? `<br/>Phone: ${this._esc(company.phone)}`       : '',
            company.email    ? `<br/>Email: ${this._esc(company.email)}`       : '',
            company.address  ? `<br/>Address: ${this._esc(company.address)}`   : '',
            company.building ? `<br/>Location: ${this._esc(company.building)}` : '',
            company.street   ? `<br/>${this._esc(company.street)}`             : '',
            company.town     ? `<br/>Town: ${this._esc(company.town)}`         : '',
            company.krapin   ? `<br/>PIN: ${this._esc(company.krapin)}`        : '',
            '</p><hr/>'
          );
        }

        if (this.order.customer) {
          const cu = this.order.customer;
          rows.push(
            '<h6>Bill To:</h6>',
            `<p>Name: ${this._esc(cu.full_name)}</p>`,
            cu.phone   ? `<p>Phone: ${this._esc(cu.phone)}</p>`     : '',
            cu.email   ? `<p>Email: ${this._esc(cu.email)}</p>`     : '',
            cu.company ? `<p>Company: ${this._esc(cu.company)}</p>` : '',
            cu.krapin  ? `<p>Pin: ${this._esc(cu.krapin)}</p>`      : '',
            this.customerStreet(cu) ? `<p>Address: ${this._esc(this.customerStreet(cu))}</p>` : '',
            cu.town    ? `<p>Town: ${this._esc(cu.town)}</p>`       : '',
            '<hr/>'
          );
        }

        rows.push(
          `<p>Order Number: ${this._esc(this.order.order)}</p>`,
          `<p>Date: ${moment(this.order.created_at).format('D-MM-Y')}</p>`,
          `<p>Time: ${moment(this.order.created_at).format('h:mm a')}</p>`,
          '<hr/>',
          `<table><thead><tr><th>QTY</th><th>Description</th><th>${currency}</th><th>Totals</th></tr></thead><tbody>`
        );

        for (const item of this.orderitems) {
          const name      = this._esc(getItemName(item));
          const unitPrice = getItemUnitPrice(item);
          const lineTotal = (unitPrice * item.qty).toFixed(2);
          // Product Variations + Units/Packaging — only shown for a line
          // actually sold in a non-implicit unit; a plain base-unit sale
          // renders exactly as before. See
          // docs/product-variations-units-implementation-plan.md Part 11.
          const unitNote = (item.unit_label && item.conversion_factor_snapshot && parseFloat(item.conversion_factor_snapshot) !== 1)
            ? `<br><small>${item.qty} ${this._esc(item.unit_label)} (${item.base_quantity != null ? item.base_quantity : (item.qty * item.conversion_factor_snapshot)} pcs)</small>`
            : '';
          rows.push(`<tr><td>${item.qty}</td><td>${name}${unitNote}</td><td>${unitPrice.toFixed(2)}</td><td>${lineTotal}</td></tr>`);
        }

        const discountRow = this.order.discount != null
          ? `<tr><td colspan="3">Discount</td><td>${parseFloat(this.order.discount).toFixed(2)}</td></tr>` : '';
        const taxRows = (this.storeSettings.enable_taxes && this.order.tax_ledgers?.length)
          ? this.order.tax_ledgers.map(t => `<tr><td colspan="3">${this._esc(t.description)}</td><td>${parseFloat(t.credit).toFixed(2)}</td></tr>`).join('')
          : '';
        const balanceRow = (this.order.partial && this.order.balance > 0)
          ? `<tr><td colspan="3">Balance</td><td>${parseFloat(this.order.balance).toFixed(2)}</td></tr>` : '';

        rows.push(
          `</tbody><tfoot>`,
          `<tr style="border-top:1px solid #000"><td colspan="3">${this.totalQuantity} Items</td><td></td></tr>`,
          discountRow,
          `<tr><td colspan="3">Sub Total</td><td>${this.correctSubTotal.toFixed(2)}</td></tr>`,
          taxRows,
          balanceRow,
          `<tr style="border-top:2px solid #000"><td colspan="3"><b>TOTAL</b></td><td><b>${parseFloat(this.order.total_amount).toFixed(2)}</b></td></tr>`,
          `</tfoot></table>`
        );

        if (this.order.partial && this.order.balance > 0) {
          rows.push(`<div class="tc" style="color:red"><h1>NOT CLEARED</h1></div>`);
        }

        if (this.order.paymentmethod) {
          rows.push(
            `<h5>Payment</h5>`,
            `<p>Payment: ${this._esc(this.order.paymentmethod.payment_method)}</p>`,
            `<p>Amount: ${this.order.total_amount}</p>`
          );
          if (parseFloat(this.order.cash_received || 0) > 0) {
            rows.push(
              `<p>Cash Received: ${parseFloat(this.order.cash_received).toFixed(2)}</p>`,
              `<p>Change Given: ${parseFloat(this.order.change_given ?? 0).toFixed(2)}</p>`
            );
          }
          if (this.order.transaction_validated?.transaction_code) {
            rows.push(`<p>Txn Code: ${this._esc(this.order.transaction_validated.transaction_code)}</p>`);
          }
        }

        if (this.order.ordernotes?.length) {
          rows.push('<ul>');
          for (const n of this.order.ordernotes) {
            if (n.on_receipt) rows.push(`<li>${this._esc(n.note)}</li>`);
          }
          rows.push('</ul>');
        }

        rows.push(
          `<hr/><div class="footer tca"><p>You were served by: <span class="bold">${this._esc(this.order.soldby?.name || '')}</span></p><hr/>`
        );
        if (company?.configs) rows.push(`<p class="tj bold">${company.configs}</p>`);
        if (this.storeSettings?.receipt_footer_message) {
          rows.push(`<div class="footer"><small>${this._esc(this.storeSettings.receipt_footer_message)}</small></div>`);
        }

        const ft = this.order.fiscal_transaction;
        const qrImgSrc = qrSrc || base64QR;
        if (qrImgSrc || ft?.control_serial || ft?.control_unit_info) {
          rows.push(`<div class="ci">`);
          if (qrImgSrc) rows.push(`<div class="qr"><img src="${qrImgSrc}" alt="QR"/></div>`);
          if (ft?.control_serial || ft?.control_unit_info) {
            rows.push(`<p><b>Control Info</b></p>`);
            if (ft.control_datetime)        rows.push(`<p>Date: ${this._esc(ft.control_datetime)}</p>`);
            if (ft.control_invoice_number)  rows.push(`<p>Invoice #: ${this._esc(ft.control_invoice_number)}</p>`);
            if (ft.control_serial)          rows.push(`<p>Serial: ${this._esc(ft.control_serial)}</p>`);
            if (ft.control_unit_info)       rows.push(`<p>CU Info: ${this._esc(ft.control_unit_info)}</p>`);
          }
          rows.push('</div>');
        }

        rows.push('</div>');

      } else {
        // Kitchen HTML — one ticket per station, each on its own cut
        this.kitchenTickets.forEach((t, idx) => {
          rows.push(
            `<div${idx < this.kitchenTickets.length - 1 ? ' style="page-break-after:always"' : ''}>`,
            `<div class="tc">`,
            t.flag ? `<h4>** ${this._esc(t.flag)} **</h4>` : '',
            `<h4>Kitchen Order</h4>`,
            t.station ? `<h4>${this._esc(t.station.toUpperCase())}</h4>` : '',
            `<h4>Order: ${this._esc(this.order.order)}</h4></div><hr/>`,
            `<p><b>Order By:</b> <span class="bold">${this._esc(this.order.soldby?.name || '')}</span></p>`,
            `<p>Sent: ${moment(t.sentAt).format('D-MM-Y h:mm a')}</p>`,
            `<table style="table-layout:fixed"><thead><tr><th style="width:12mm">QTY</th><th>Description</th></tr></thead><tbody>`
          );
          for (const item of t.items) {
            const note = item.notes ? `<br/><span style="font-size:10pt;font-weight:400">&gt; ${this._esc(item.notes)}</span>` : '';
            rows.push(`<tr style="font-size:13pt;font-weight:700"><td>${this._esc(item.qty)}</td><td>${this._esc(item.name)}${note}</td></tr>`);
          }
          rows.push(`<tr><td colspan="2"><b>Total QTY: ${t.totalQty}</b></td></tr></tbody></table>`);
          for (const n of this.kitchenNotes) rows.push(`<p><b>NOTE:</b> ${this._esc(n)}</p>`);
          rows.push('</div>');
        });
      }

      return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"><style>${CSS}</style></head><body><div>${rows.join('')}</div></body></html>`;
    },

    // HTML-escape user-supplied strings to prevent XSS in the print iframe
    _esc (str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    // ══════════════════════════════════════════════════════════════════════
    // IMAGE CACHE  (L1: in-memory Map  |  L2: IndexedDB, 7-day TTL)
    // getImageDB() returns a shared DB promise — opened only once per session.
    // ══════════════════════════════════════════════════════════════════════

    async _initImageDB () {
      try { await getImageDB(); this._cleanOldCache(); }
      catch (e) { console.warn('[invoice] IndexedDB unavailable:', e); }
    },

    async _fetchImage (url, maxWidth = 300, retries = 3) {
      // L1 — memory
      const memKey = `${url}@${maxWidth}`;
      if (this._imageMemCache[memKey]) return this._imageMemCache[memKey];

      // L2 — IndexedDB
      const cached = await this._dbGetImage(url);
      if (cached) { this._imageMemCache[memKey] = cached; return cached; }

      // Fetch → resize → cache (with retries, matching the old fetchAndProcessImage behaviour)
      let lastErr;
      for (let attempt = 0; attempt < retries; attempt++) {
        this.imageLoadingStatus = attempt === 0 ? 'Loading image…' : `Loading image (attempt ${attempt + 1}/${retries})…`;
        try {
          const r = await fetch(url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const raw  = await r.blob();
          // Defensively re-type blobs with missing/wrong MIME (e.g. R2 serving
          // octet-stream). Sniff format from magic bytes so we don't corrupt
          // JPEG logos by forcing image/png.
          let blob = raw;
          if (!raw.type.startsWith('image/')) {
            const buf = await raw.arrayBuffer();
            const sig = new Uint8Array(buf, 0, 4);
            const type = sig[0] === 0x89 ? 'image/png'   // \x89PNG
                       : sig[0] === 0x47 ? 'image/gif'   // GIF8
                       : 'image/jpeg';                    // FF D8 FF … + WebP fallback
            blob = new Blob([buf], { type });
          }
          const b64  = await new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onloadend = () => res(reader.result);
            reader.onerror   = rej;
            reader.readAsDataURL(blob);
          });
          const resized = await this._resizeImage(b64, maxWidth);
          this._imageMemCache[memKey] = resized;
          this._dbSetImage(url, resized); // fire-and-forget
          this.imageLoadingStatus = '';
          return resized;
        } catch (e) {
          lastErr = e;
          console.warn(`[invoice] image fetch attempt ${attempt + 1}/${retries} failed:`, e.message, url);
          if (attempt < retries - 1) {
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          }
        }
      }
      this.imageLoadingStatus = '';
      throw lastErr;
    },

    _resizeImage (base64, maxWidth = 300) {
      return new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => {
          const scale = maxWidth / img.width;
          const c     = document.createElement('canvas');
          c.width     = maxWidth;
          c.height    = img.height * scale;
          c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
          res(c.toDataURL('image/png'));
        };
        img.onerror = rej;
        img.src = base64;
      });
    },

    async _dbGetImage (url) {
      try {
        const db = await getImageDB();
        return new Promise(res => {
          const req = db.transaction(['images'], 'readonly').objectStore('images').get(url);
          req.onsuccess = () => {
            const r = req.result;
            if (r && Date.now() - r.timestamp < 7 * 86400000) {
              res(r.base64);
            } else {
              if (r) this._dbDeleteImage(url);
              res(null);
            }
          };
          req.onerror = () => res(null);
        });
      } catch { return null; }
    },

    async _dbSetImage (url, base64) {
      try {
        const db = await getImageDB();
        return new Promise(res => {
          const tx = db.transaction(['images'], 'readwrite');
          tx.objectStore('images').put({ url, base64, timestamp: Date.now() });
          tx.oncomplete = res;
          tx.onerror    = res;
        });
      } catch { /* non-fatal */ }
    },

    async _dbDeleteImage (url) {
      try {
        const db = await getImageDB();
        const tx = db.transaction(['images'], 'readwrite');
        tx.objectStore('images').delete(url);
      } catch { /* non-fatal */ }
    },

    async _cleanOldCache () {
      try {
        const db     = await getImageDB();
        const tx     = db.transaction(['images'], 'readwrite');
        const store  = tx.objectStore('images');
        const cutoff = Date.now() - 7 * 86400000;
        store.index('timestamp').openCursor().onsuccess = e => {
          const cur = e.target.result;
          if (!cur) return;
          if (cur.value.timestamp < cutoff) store.delete(cur.value.url);
          cur.continue();
        };
      } catch { /* non-fatal */ }
    },

    // Public aliases — kept for any external callers
    async initImageCache ()              { await this._initImageDB(); },
    async queueImageProcessing (u, w)    { return this._fetchImage(u, w); },
    async getCachedImage (url)           { return this._dbGetImage(url); },
    async setCachedImage (url, b64)      { return this._dbSetImage(url, b64); },
    async deleteCachedImage (url)        { return this._dbDeleteImage(url); },
    async cleanOldCache ()               { return this._cleanOldCache(); },
    resizeImage (b64, w)                 { return this._resizeImage(b64, w); },
    async fetchImageAsBase64 (url) {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      return new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.onerror   = rej;
        reader.readAsDataURL(blob);
      });
    },

    // ══════════════════════════════════════════════════════════════════════
    // TIMS / FISCAL POLLING
    // ══════════════════════════════════════════════════════════════════════

    checkTimsReadiness () {
      const urlParams = new URLSearchParams(window.location.search);
      const ft = this.order?.fiscal_transaction;

      // Auto-enable polling when the order already has a fiscal_transaction
      // row but the QR hasn't been generated yet (async job still running).
      // Without this, triggerPrint() would fire immediately and the first
      // print would have no QR; only a manual re-print would include it.
      // Skip polling when the transaction already reached a terminal state
      // (failed / completed) with no QR — nothing more to wait for.
      const terminalStatus = ft?.status === 'failed' || ft?.status === 'completed';
      const needsPoll = ft && !ft.qr_url?.trim() && !terminalStatus;

      if (urlParams.get('tims') !== '1' && !needsPoll) {
        this.triggerPrint();
        return;
      }

      this.hasTims = true;
      const uuid   = window.location.pathname.split('/').pop();
      let attempts = 0;
      const MAX    = 10;
      this.fiscalLoadingStatus = 'Waiting for fiscal device…';

      this.fiscalPollingInterval = setInterval(async () => {
        attempts++;
        try {
          const { data: { data: fd } } = await axios.get(`/api/fiscal/receipts/${uuid}`);
          const done  = fd.completed || fd.status === 'completed' || fd.success;
          const hasQR = !!fd.qr_url?.trim();

          if (done && hasQR) {
            this._applyFiscalData(fd);
            clearInterval(this.fiscalPollingInterval);
            this.fiscalPollingInterval = null;
            this.isFiscalReady         = true;
            this.hasTims               = false;
            this.fiscalLoadingStatus   = 'Fiscal data ready!';
            // Bust the QR image cache so _htmlPrint fetches the newly generated
            // image rather than the empty/stale entry from a previous attempt.
            if (fd.qr_url) {
              const qrProxyUrl = `/api/proxy-logo?remote_url=${encodeURIComponent(fd.qr_url)}`;
              const memKey     = `${qrProxyUrl}@200`;
              delete this._imageMemCache[memKey];
              this._dbDeleteImage(qrProxyUrl);
            }
            this.triggerPrint();
          } else if (fd.failed || fd.status === 'failed' || attempts >= MAX) {
            clearInterval(this.fiscalPollingInterval);
            this.fiscalPollingInterval = null;
            this.hasTims               = false;
            this.fiscalLoadingStatus   = attempts >= MAX
              ? 'Fiscal timeout — printing anyway'
              : 'Fiscal failed — printing without QR';
            this.triggerPrint();
          } else {
            this.fiscalLoadingStatus = `Waiting for fiscal device… (${attempts}/${MAX})`;
          }
        } catch (e) {
          console.error('[invoice] fiscal poll error:', e);
          if (attempts >= MAX) {
            clearInterval(this.fiscalPollingInterval);
            this.fiscalPollingInterval = null;
            this.hasTims               = false;
            this.fiscalLoadingStatus   = 'Fiscal error — printing anyway';
            this.triggerPrint();
          }
        }
      }, 2000);
    },

    _applyFiscalData (fd) {
      if (!this.order) return;
      // Vue 2 cannot track property additions on nested objects that were not
      // originally declared reactive (fiscal_transaction arrives via prop and
      // may be a plain server object). The ONLY reliable fix is to replace the
      // entire fiscal_transaction value via $set — this forces Vue to observe
      // the new object and triggers re-renders in both the template and the
      // computed properties that read from it.
      const existing = this.order.fiscal_transaction || {};
      const merged = Object.assign({}, existing, {
        qr_url:                 fd.qr_url                 || existing.qr_url,
        control_serial:         fd.control_serial         || existing.control_serial,
        control_datetime:       fd.control_datetime       || existing.control_datetime,
        control_unit_info:      fd.control_unit_info      || existing.control_unit_info,
        control_invoice_number: fd.control_invoice_number || existing.control_invoice_number,
        raw_response:           fd.raw_response           || existing.raw_response,
      });
      // $set on the parent prop replaces fiscal_transaction with a new observed
      // object — Vue 2 will now react to all its properties correctly.
      this.$set(this.order, 'fiscal_transaction', merged);
    },

    // ══════════════════════════════════════════════════════════════════════
    // KDS
    // ══════════════════════════════════════════════════════════════════════

    async _fetchKitchenOrders (ids) {
      const results = await Promise.all(ids.map(id =>
        axios.get(`/api/kds/show/${id}`)
          .then(r => r.data)
          .catch(e => { console.warn('[invoice] KDS fetch error:', e); return null; })
      ));
      this.kitchenOrders = results.filter(Boolean);
    },

    // ══════════════════════════════════════════════════════════════════════
    // SETTINGS
    // ══════════════════════════════════════════════════════════════════════

    getStoreSettings () {
      this.storeSettings = this.userDetails.company.companysettings;
      this.checkTimsReadiness();
      if (this.walletEnabled && this.order?.customer?.id) {
        this._loadWalletBalance(this.order.customer.id);
      }
    },

    async _loadWalletBalance (customerId) {
      try {
        const { data } = await axios.get(`/api/subscriptions/wallet/${customerId}`);
        this.walletBalance = data.balance;
      } catch (e) {
        this.walletBalance = null;
      }
    },

    async payWithWallet () {
      this.walletPaymentError = '';
      const amount = parseFloat(this.walletPaymentAmount);
      if (!amount || amount <= 0) {
        this.walletPaymentError = 'Enter a valid amount.';
        return;
      }
      this.walletPaymentLoading = true;
      try {
        const { data } = await axios.post(`/api/orders/${this.order.id}/payment/wallet`, { amount });
        this.$set(this.order, 'balance', data.balance);
        this.walletBalance       = data.wallet_balance;
        this.walletPaymentAmount = '';
        if (data.balance <= 0) {
          this.$set(this.order, 'partial', false);
        }
      } catch (e) {
        this.walletPaymentError = e.response?.data?.message || 'Wallet payment failed.';
      } finally {
        this.walletPaymentLoading = false;
      }
    },

    async generateFiscalReceipt () {
      if (this.fiscalActionLoading) return;
      this.fiscalActionLoading = true;
      try {
        const type = this.order?.customer?.krapin ? 'invoice' : 'sale';
        const { data } = await axios.post(`/api/fiscal/orders/${this.order.id}/receipt`, { transaction_type: type });
        if (data.success) {
          this.$bvToast?.toast('Fiscal receipt queued for processing.', { title: 'Fiscal', variant: 'success', solid: true });
          this.$set(this.order, 'fiscal_transaction', { status: 'pending', transaction_type: type });
          this.checkTimsReadiness();
        } else {
          this.$bvToast?.toast(data.message || 'Failed to generate fiscal receipt.', { title: 'Error', variant: 'danger', solid: true });
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to generate fiscal receipt.';
        this.$bvToast?.toast(msg, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.fiscalActionLoading = false;
      }
    },

    async issueCreditNote () {
      if (this.fiscalActionLoading) return;
      const ok = this.$bvModal
        ? await this.$bvModal.msgBoxConfirm(
            'Issue a fiscal credit note to reverse the receipt for this order? This cannot be undone.',
            { title: 'Issue Credit Note', okVariant: 'warning', okTitle: 'Issue Credit Note', cancelTitle: 'Cancel' }
          )
        : window.confirm('Issue a fiscal credit note to reverse this receipt? This cannot be undone.');
      if (!ok) return;
      this.fiscalActionLoading = true;
      try {
        const { data } = await axios.post(`/api/fiscal/orders/${this.order.id}/credit-note`);
        if (data.success) {
          this.$bvToast?.toast('Credit note queued for processing.', { title: 'Credit Note', variant: 'success', solid: true });
          this.$set(this.order, 'fiscal_transaction', { status: 'pending', transaction_type: 'credit_note' });
        } else {
          this.$bvToast?.toast(data.message || 'Failed to issue credit note.', { title: 'Error', variant: 'danger', solid: true });
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to issue credit note.';
        this.$bvToast?.toast(msg, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.fiscalActionLoading = false;
      }
    },

    // ══════════════════════════════════════════════════════════════════════
    // PRINT TRACKING
    // ══════════════════════════════════════════════════════════════════════

    async _loadPrintStatus () {
      if (!this.order?.id || this.isKitchenReceipt) return;
      try {
        const { data } = await axios.get(`/api/orders/${this.order.id}/print-status`);
        this.printStatus = {
          printCount: data.print_count ?? 0,
          limit:      data.limit       ?? null,
          blocked:    data.blocked     ?? false,
        };
      } catch (e) {
        console.warn('[invoice] could not load print status:', e);
      }
    },

    // credentials: { employee_code, employee_pin } — supplied once the limit
    // is hit and the approval modal has been filled in. Retries once per
    // approval attempt; a wrong PIN surfaces as a normal print failure that
    // the user can retry by clicking Print again.
    async _recordPrint (credentials = null) {
      try {
        const payload = credentials
          ? { employee_code: credentials.code, employee_pin: credentials.pin }
          : {};
        const { data } = await axios.post(`/api/orders/${this.order.id}/print-log`, payload);
        // Update local state so button reflects the new count immediately
        this.printStatus.printCount = data.print_count;
        this.printStatus.limit      = data.limit;
        this.printStatus.blocked    = false; // just printed successfully, not blocked yet
      } catch (err) {
        if (err.response?.status === 403 && err.response.data?.requires_approval) {
          this.printStatus.blocked = true;
          const approved = await this._promptPrintApproval();
          if (!approved) {
            throw new Error('Print cancelled — employee approval required.');
          }
          return this._recordPrint(approved);
        }
        if (err.response?.status === 401 || err.response?.status === 403) {
          // Invalid credentials or an employee without override rights
          throw new Error(err.response.data?.message || 'Print approval failed.');
        }
        // Fail closed: this call is the server-authoritative gate for the print
        // limit. If we can't confirm it succeeded (network drop, 404 from a
        // company/order scoping mismatch, 500, etc.) we must NOT proceed to
        // dispatch the printer — doing so would let the limit be bypassed
        // simply by making the record call error out. This is unrelated to
        // the print limit itself, so don't blame the limit in the message.
        console.error('[invoice] print-log record failed:', err);
        throw new Error(err.response?.data?.message || 'Print failed — please try again.');
      }
    },

    // Shows the employee code + PIN modal and resolves with the entered
    // credentials on approval, or null if the user cancels.
    _promptPrintApproval () {
      this.printApproval.code  = '';
      this.printApproval.pin   = '';
      this.printApproval.error = '';
      this.$bvModal.show('print-approval-modal');
      return new Promise((resolve) => {
        this.printApproval.resolve = resolve;
      });
    },

    _confirmPrintApproval () {
      if (!this.printApproval.code || !this.printApproval.pin) {
        this.printApproval.error = 'Enter both an employee code and PIN.';
        return;
      }
      const credentials = { code: this.printApproval.code, pin: this.printApproval.pin };
      this.$bvModal.hide('print-approval-modal');
      this.printApproval.resolve?.(credentials);
      this.printApproval.resolve = null;
    },

    _cancelPrintApproval () {
      this.$bvModal.hide('print-approval-modal');
      this.printApproval.resolve?.(null);
      this.printApproval.resolve = null;
    },

    // Public aliases kept for backwards-compat with generatePrintContent callers
    generatePrintContent (base64Logo, base64QR) { return this._buildHtml(base64Logo, base64QR); },
    async generatePrintContentBluetooth (logo, qr) { return this._buildBluetoothEscPos(logo, qr); },
    async printReceipt () { return this._blePrint(); },
    async printInvoice () { return this._htmlPrint(); },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container { margin: 0 auto; }
.text-center { text-align: center; }
.text-justify { text-align: justify !important; }
.logo { text-align: center; margin-bottom: 10px; }
.logo img { max-width: 120px; max-height: 120px; }
.table { width: 100%; margin-bottom: 10px; }
.table th, .table td { padding: 5px; text-align: left; border-bottom: 1px solid #000; }
.footer { margin-top: 10px; text-align: center; }
.btn-primary:disabled {
  color: #000;
  background-color: #d58571;
  border-color: #d58571;
}
</style>
